// ARCA-749 (ARCA-717 WS3) — client mirror of the backend's `splitWireGrids`
// (apps/backend/src/match/view/player-view.ts).
//
// After the grid decoupling (Option 2) the wire carries THREE board projections:
//   • `board`       — the TRANSITIONAL combined grid (pieces overlaid on word
//                     cells 0..35) that legacy consumers keep reading.
//   • `chess_grid`  — the FIXED 6×6 chess board (exactly 36 cells), piece + role
//                     state only. PieceLayer renders pieces off this.
//   • `word_field`  — the growing word/Runespring surface (36 → 48 → 60 cells),
//                     letter + rarity + anchor/hollow/freeze only. LetterLayer +
//                     AnchorPlacementOverlay render off this.
//
// The live backend always emits `chess_grid` / `word_field`, but the ~30
// partial-board PlayerView fixtures predate them. These resolvers return the
// wire field when present and otherwise DERIVE it from the combined `board` —
// the exact same structural slice the backend's `splitWireGrids` performs, so a
// fixture that only supplies `board` renders identically to a live wire payload.

import { CHESS_CELLS } from './layers/board-layout'
import type { PlayerView } from './PlayerViewSchema'

/** The fixed 6×6 chess-grid cell shape (piece + role state only). */
export type ChessGridCell = NonNullable<PlayerView['chess_grid']>[number]
/** The growing word-field cell shape (word surface only — no piece columns). */
export type WordFieldCell = NonNullable<PlayerView['word_field']>[number]

/**
 * The FIXED 6×6 chess grid for a PlayerView. Returns the wire `chess_grid` when
 * present; otherwise derives it from the combined `board` by slicing cells
 * 0..{@link CHESS_CELLS}-1 and projecting the piece columns — mirroring the
 * backend slice so derived and wire grids are byte-identical.
 */
export function resolveChessGrid(
  playerView: PlayerView,
): ReadonlyArray<ChessGridCell> {
  if (playerView.chess_grid !== undefined) return playerView.chess_grid
  return playerView.board
    .filter((c) => c.cell_idx < CHESS_CELLS)
    .map((c) => ({
      cell_idx: c.cell_idx,
      piece_owner_slot: c.piece_owner_slot,
      piece_kind: c.piece_kind,
      role_kind: c.role_kind,
      role_expires_turn: c.role_expires_turn,
      role_extensions_used: c.role_extensions_used,
      can_extend: c.can_extend,
    }))
}

/**
 * The GROWING word field for a PlayerView. Returns the wire `word_field` when
 * present; otherwise derives it from the combined `board` (every cell, word
 * surface only — piece columns dropped), mirroring the backend slice. The
 * derived `is_frozen` defaults to `false` for older fixtures that omit it
 * (consumers treat missing as not-frozen — server is authoritative).
 */
export function resolveWordField(
  playerView: PlayerView,
): ReadonlyArray<WordFieldCell> {
  if (playerView.word_field !== undefined) return playerView.word_field
  return playerView.board.map((c) => ({
    cell_idx: c.cell_idx,
    tier: c.tier,
    tint: c.tint,
    is_anchor: c.is_anchor,
    anchor_owner: c.anchor_owner,
    is_hollow: c.is_hollow,
    is_frozen: c.is_frozen === true,
    letter: c.letter,
  }))
}
